/**
 * Early Divergence Watchlist — "Watchlist phân kì sớm"
 *
 * Finds stocks *starting* to diverge from the market — catches early
 * signals before they become obvious in the confirmed scanner.
 */
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  IconButton, TextField, InputAdornment, Skeleton,
} from '@mui/material';
import {
  Refresh, Close, Search, TrendingUp, TrendingDown,
  ArrowUpward, ArrowDownward, CalendarToday,
  FiberManualRecord, Visibility, TrackChanges, Radar,
  ShowChart, Public, Speed, Bolt, Psychology,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { earlyDivergenceAPI } from '../services/api';

// ── Helpers ──────────────────────────────────────────────────────────
const fmtPct = (n, d = 2) => n != null ? `${n >= 0 ? '+' : ''}${(n * 100).toFixed(d)}%` : '—';
const fmtScore = (n) => n != null ? (n * 100).toFixed(1) : '—';
const fmtBillion = (n) => n != null ? `${(n / 1e9).toFixed(1)} tỷ` : '—';

const EARLY_SETUP_CONFIG = {
  rs_turning:         { color: '#3b82f6', icon: <ShowChart sx={{ fontSize: 14 }} /> },
  accumulation_hint:  { color: '#f59e0b', icon: <Psychology sx={{ fontSize: 14 }} /> },
  support_holding:    { color: '#10b981', icon: <Speed sx={{ fontSize: 14 }} /> },
  momentum_building:  { color: '#8b5cf6', icon: <Bolt sx={{ fontSize: 14 }} /> },
  emerging:           { color: '#64748b', icon: <Radar sx={{ fontSize: 14 }} /> },
};

// ── Skeletons ────────────────────────────────────────────────────────
const SummaryStripSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
    <Box sx={{ minWidth: 260, borderRadius: 2.5, border: '1px solid #f1f5f9', bgcolor: '#fff', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2, flexShrink: 0 }} />
      <Box sx={{ display: 'flex', gap: 2.5 }}>
        <Box><Skeleton width={60} height={10} sx={{ mb: 0.5 }} /><Skeleton width={80} height={24} /></Box>
        <Box><Skeleton width={24} height={10} sx={{ mb: 0.5 }} /><Skeleton width={80} height={24} /></Box>
      </Box>
    </Box>
    {[0, 1, 2, 3].map(i => (
      <Box key={i} sx={{ flex: '1 1 120px', minWidth: 110, borderRadius: 2.5, border: '1px solid #f1f5f9', bgcolor: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
        <Box><Skeleton width={50} height={10} sx={{ mb: 0.5 }} /><Skeleton width={40} height={22} /></Box>
      </Box>
    ))}
  </Box>
);

const SearchBarSkeleton = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Skeleton variant="rounded" width={180} height={32} sx={{ borderRadius: 2 }} />
      {[0, 1, 2].map(i => (
        <Skeleton key={i} variant="rounded" width={85} height={24} sx={{ borderRadius: 1.5 }} />
      ))}
    </Box>
    <Skeleton width={50} height={16} />
  </Box>
);

const TableSkeleton = () => (
  <Box sx={{ borderRadius: 2, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', gap: 0, bgcolor: '#f8fafc', px: 2, py: 1.2 }}>
      {[24, 50, 60, 48, 60, 60, 52, 36, 80, 200].map((w, i) => (
        <Box key={i} sx={{ width: w, flexShrink: 0, ...(i === 9 && { flex: 1 }) }}>
          <Skeleton width={w * 0.7} height={12} />
        </Box>
      ))}
    </Box>
    {[...Array(10)].map((_, row) => (
      <Box key={row} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid #f8fafc' }}>
        <Box sx={{ width: 24, flexShrink: 0 }}><Skeleton width={16} height={14} /></Box>
        <Box sx={{ width: 50, flexShrink: 0 }}><Skeleton width={36} height={16} /></Box>
        <Box sx={{ width: 60, flexShrink: 0 }}><Skeleton width={50} height={14} /></Box>
        <Box sx={{ width: 48, flexShrink: 0 }}><Skeleton variant="rounded" width={38} height={20} sx={{ borderRadius: 1 }} /></Box>
        <Box sx={{ width: 60, flexShrink: 0 }}><Skeleton width={50} height={14} /></Box>
        <Box sx={{ width: 60, flexShrink: 0 }}><Skeleton width={50} height={14} /></Box>
        <Box sx={{ width: 52, flexShrink: 0 }}><Skeleton width={14} height={14} /></Box>
        <Box sx={{ width: 36, flexShrink: 0 }}><Skeleton width={20} height={14} /></Box>
        <Box sx={{ width: 80, flexShrink: 0 }}><Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: 3 }} /></Box>
        <Box sx={{ flex: 1 }}><Skeleton width={`${60 + Math.random() * 30}%`} height={14} /></Box>
      </Box>
    ))}
  </Box>
);

const EmptyState = ({ title, sub }) => (
  <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
    <Visibility sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#475569' }}>{title}</Typography>
    {sub && <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>{sub}</Typography>}
  </Box>
);

// ═════════════════════════════════════════════════════════════════════
export default function EarlyDivergenceWatchlist() {
  const { t } = useTranslation();
  const T = useCallback((key, opts) => t(`earlyDivergence.${key}`, opts), [t]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortCol, setSortCol] = useState('watchlist_rank');
  const [sortDir, setSortDir] = useState('desc');
  const [detailTicker, setDetailTicker] = useState(null);
  const didInitialLoad = React.useRef(false);

  const runScan = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await earlyDivergenceAPI.scan({ top_n: 40 });
      setData(res);
      setIsFirstLoad(false);
    } catch (e) {
      setError(e?.response?.data?.detail || T('error'));
    } finally {
      setLoading(false);
    }
  }, [T]);

  React.useEffect(() => {
    if (!didInitialLoad.current) {
      didInitialLoad.current = true;
      runScan();
    }
  }, [runScan]);

  const results = data?.results || [];
  const summary = data?.summary || {};

  const filtered = results
    .filter(r => !searchTerm || r.ticker?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const va = a[sortCol] ?? -Infinity;
      const vb = b[sortCol] ?? -Infinity;
      return sortDir === 'desc' ? vb - va : va - vb;
    });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return null;
    return sortDir === 'desc'
      ? <ArrowDownward sx={{ fontSize: 12, ml: 0.3 }} />
      : <ArrowUpward sx={{ fontSize: 12, ml: 0.3 }} />;
  };

  const detailRow = detailTicker ? results.find(r => r.ticker === detailTicker) : null;

  const getSetupCfg = (key) => EARLY_SETUP_CONFIG[key] || EARLY_SETUP_CONFIG.emerging;

  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)',
          }}>
            <Visibility sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {T('title')}
              </Typography>
              {summary?.scan_date && (
                <Chip
                  icon={<CalendarToday sx={{ fontSize: 10 }} />}
                  label={summary.scan_date.split(' ')[0]}
                  size="small"
                  sx={{ fontSize: '0.68rem', fontWeight: 600, height: 22, bgcolor: alpha('#f59e0b', 0.08), color: '#d97706', '& .MuiChip-icon': { color: '#d97706' } }}
                />
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {T('subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
          disabled={loading}
          onClick={runScan}
          sx={{
            textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
            bgcolor: '#f59e0b', color: '#fff', px: 2,
            '&:hover': { bgcolor: '#d97706' },
            '&:disabled': { bgcolor: '#94a3b8', color: '#fff' },
          }}
        >
          {loading ? T('scanning') : (data ? T('rescan') : T('scanNow'))}
        </Button>
      </Box>

      {/* ── Market Pulse + Stats ─────────────────────────────── */}
      {isFirstLoad ? (
        <SummaryStripSkeleton />
      ) : data && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>

          {/* Market Pulse */}
          <Box sx={{
            position: 'relative', overflow: 'hidden',
            minWidth: 260, borderRadius: 2.5, px: 2.5, py: 1.8,
            bgcolor: '#fff', border: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', gap: 2.5,
          }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(summary.market_weak ? '#ef4444' : '#10b981', 0.06)} 0%, transparent 70%)` }} />
            <Box sx={{
              width: 40, height: 40, borderRadius: 2, flexShrink: 0,
              background: summary.market_weak
                ? `linear-gradient(135deg, #ef4444 0%, ${alpha('#ef4444', 0.7)} 100%)`
                : `linear-gradient(135deg, #10b981 0%, ${alpha('#10b981', 0.7)} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: summary.market_weak ? `0 3px 8px ${alpha('#ef4444', 0.25)}` : `0 3px 8px ${alpha('#10b981', 0.25)}`,
            }}>
              {summary.market_weak
                ? <TrendingDown sx={{ fontSize: 20, color: '#fff' }} />
                : <TrendingUp sx={{ fontSize: 20, color: '#fff' }} />
              }
            </Box>
            <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-end', position: 'relative' }}>
              <Box>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', lineHeight: 1, mb: 0.3 }}>
                  VNINDEX 5D
                </Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, fontFamily: 'monospace', lineHeight: 1, color: (summary.index_ret_5d || 0) < 0 ? '#ef4444' : '#10b981' }}>
                  {fmtPct(summary.index_ret_5d)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', lineHeight: 1, mb: 0.3 }}>10D</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, fontFamily: 'monospace', lineHeight: 1, color: (summary.index_ret_10d || 0) < 0 ? '#ef4444' : '#10b981' }}>
                  {fmtPct(summary.index_ret_10d)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Stat tiles */}
          {[
            { label: T('tiles.universe'), val: summary.total_universe, color: '#64748b', Icon: Public },
            { label: T('tiles.watchlist'), val: summary.watchlist_count, color: '#f59e0b', Icon: Visibility },
            { label: T('tiles.nearConfirm'), val: summary.near_confirm_count ?? 0, color: '#10b981', Icon: TrackChanges },
            { label: T('tiles.avgScore'), val: fmtScore(summary.avg_score), color: '#6366f1', Icon: Radar },
          ].map((s, i) => (
            <Box key={i} sx={{
              position: 'relative', overflow: 'hidden',
              flex: '1 1 120px', minWidth: 110, borderRadius: 2.5,
              border: `1px solid ${alpha(s.color, 0.12)}`, bgcolor: '#fff',
              px: 2, py: 1.5, transition: 'all 0.2s',
              '&:hover': { borderColor: alpha(s.color, 0.3), boxShadow: `0 4px 16px ${alpha(s.color, 0.08)}`, transform: 'translateY(-1px)' },
            }}>
              <Box sx={{ position: 'absolute', top: 0, right: 0, width: 56, height: 56, background: `radial-gradient(circle at top right, ${alpha(s.color, 0.06)} 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                  background: `linear-gradient(135deg, ${s.color} 0%, ${alpha(s.color, 0.7)} 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 2px 6px ${alpha(s.color, 0.25)}`,
                }}>
                  <s.Icon sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', lineHeight: 1, mb: 0.3 }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, color: s.color, lineHeight: 1, fontFamily: 'monospace' }}>
                    {s.val}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Search + Setup pills ────────────────────────────────── */}
      {data && results.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              placeholder={T('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
              }}
              sx={{
                width: 180,
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8rem', borderRadius: 2, height: 32,
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&.Mui-focused fieldset': { borderColor: '#f59e0b' },
                },
              }}
            />
            {Object.entries(summary.setup_distribution || {}).map(([key, count]) => {
              const cfg = getSetupCfg(key);
              return (
                <Box key={key} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  px: 0.8, py: 0.3, borderRadius: 1.5,
                  bgcolor: alpha(cfg.color, 0.06), border: `1px solid ${alpha(cfg.color, 0.1)}`,
                }}>
                  <Box sx={{ color: cfg.color, display: 'flex', fontSize: 12 }}>{cfg.icon}</Box>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: cfg.color }}>{T(`setup.${key}`)}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: cfg.color, fontFamily: 'monospace' }}>{count}</Typography>
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>{filtered.length}</Box> / {results.length}
          </Typography>
        </Box>
      )}

      {/* ── Table ───────────────────────────────────────────────── */}
      {isFirstLoad ? (
        <>
          <SearchBarSkeleton />
          <TableSkeleton />
        </>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={data ? T('table.noResults') : T('table.startScan')}
          sub={data ? T('table.noResultsSub') : undefined}
        />
      ) : (
        <TableContainer sx={{ borderRadius: 2, border: '1px solid #f1f5f9' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.ticker')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('close')}>
                  {T('table.price')} <SortIcon col="close" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('positive_div_score')}>
                  {T('table.score')} <SortIcon col="positive_div_score" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('excess_ret_3d')}>
                  {T('table.excessRet3d')} <SortIcon col="excess_ret_3d" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('rs_slope_10')}>
                  {T('table.rsSlope')} <SortIcon col="rs_slope_10" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.greenDays')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('early_signal_count')}>
                  {T('table.signals')} <SortIcon col="early_signal_count" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.setup')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.reason')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r, i) => {
                const setup = getSetupCfg(r.early_setup);
                return (
                  <TableRow
                    key={r.ticker}
                    hover
                    onClick={() => setDetailTicker(r.ticker)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#f59e0b', 0.03) } }}
                  >
                    <TableCell sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{r.ticker}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace' }}>
                      {r.close?.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        display: 'inline-block', px: 1, py: 0.2, borderRadius: 1,
                        bgcolor: alpha('#f59e0b', 0.08), color: '#d97706',
                        fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                      }}>
                        {fmtScore(r.positive_div_score)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace',
                      color: (r.excess_ret_3d || 0) > 0 ? '#10b981' : '#ef4444',
                    }}>
                      {fmtPct(r.excess_ret_3d)}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace',
                      color: (r.rs_slope_10 || 0) > 0 ? '#10b981' : '#ef4444',
                    }}>
                      {r.rs_slope_10 != null ? r.rs_slope_10.toFixed(5) : '—'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={T('table.greenDaysTooltip')} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FiberManualRecord sx={{ fontSize: 8, color: '#10b981' }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {r.green_when_index_red_10 ?? '—'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={T('table.signalsTooltip')} arrow>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 22, height: 22, borderRadius: '50%',
                          bgcolor: alpha('#f59e0b', 0.1), color: '#d97706',
                          fontSize: '0.72rem', fontWeight: 800,
                        }}>
                          {r.early_signal_count ?? 0}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={setup.icon}
                        label={T(`setup.${r.early_setup}`)}
                        size="small"
                        sx={{
                          fontSize: '0.68rem', fontWeight: 600, height: 22,
                          bgcolor: alpha(setup.color, 0.08), color: setup.color,
                          '& .MuiChip-icon': { color: setup.color },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.7rem', color: '#64748b', minWidth: 280, whiteSpace: 'normal', lineHeight: 1.5 }}>
                        {r.early_reason}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Detail dialog ───────────────────────────────────────── */}
      <Dialog open={!!detailRow} onClose={() => setDetailTicker(null)} maxWidth="sm" fullWidth>
        {detailRow && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>{detailRow.ticker}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{T('detail.title')}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setDetailTicker(null)}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {[
                  { label: T('detail.score'), value: fmtScore(detailRow.positive_div_score), color: '#f59e0b' },
                  { label: T('detail.setup'), value: T(`setup.${detailRow.early_setup}`), color: getSetupCfg(detailRow.early_setup).color },
                  { label: T('detail.signalCount'), value: detailRow.early_signal_count ?? '—', color: '#f59e0b' },
                  { label: T('detail.rsSlope10'), value: detailRow.rs_slope_10?.toFixed(6) ?? '—', color: (detailRow.rs_slope_10 || 0) > 0 ? '#10b981' : '#ef4444' },
                  { label: T('detail.excessRet3d'), value: fmtPct(detailRow.excess_ret_3d) },
                  { label: T('detail.excessRet5d'), value: fmtPct(detailRow.excess_ret_5d) },
                  { label: T('detail.rsRet5d'), value: fmtPct(detailRow.rs_ret_5d) },
                  { label: T('detail.rsSlope20'), value: detailRow.rs_slope_20?.toFixed(6) ?? '—' },
                  { label: T('detail.greenDays'), value: detailRow.green_when_index_red_10 ?? '—' },
                  { label: T('detail.flatDays'), value: detailRow.flat_when_index_red_10 ?? '—' },
                  { label: T('detail.aboveMa10'), value: detailRow.above_ma10_10 != null ? `${(detailRow.above_ma10_10 * 100).toFixed(0)}%` : '—' },
                  { label: T('detail.aboveMa20'), value: detailRow.above_ma20_10 != null ? `${(detailRow.above_ma20_10 * 100).toFixed(0)}%` : '—' },
                  { label: T('detail.rsi'), value: detailRow.rsi14?.toFixed(1) ?? '—' },
                  { label: T('detail.beta'), value: detailRow.beta20?.toFixed(2) ?? '—' },
                  { label: T('detail.liquidity'), value: fmtBillion(detailRow.avg_value_20d), color: (detailRow.avg_value_20d || 0) >= 20e9 ? '#10b981' : '#ef4444' },
                  { label: T('detail.volRatio'), value: detailRow.vol_ratio_20?.toFixed(2) ?? '—' },
                  { label: T('detail.ret5d'), value: fmtPct(detailRow.ret_5d) },
                  { label: T('detail.ret20d'), value: fmtPct(detailRow.ret_20d) },
                ].map((item, i) => (
                  <Box key={i} sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: item.color || '#1e293b', fontFamily: 'monospace' }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Reason */}
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: alpha('#f59e0b', 0.04), border: `1px solid ${alpha('#f59e0b', 0.15)}` }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#d97706', mb: 0.5 }}>{T('detail.reasonTitle')}</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>{detailRow.early_reason}</Typography>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* ── Error snackbar ──────────────────────────────────────── */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
